import { useState } from 'react';
import apiUser from '../../../apiUser';
import { motivoAcao, situacaoChave, fmtBRL, txtRecalcular, txtReenviarMesmo, txtFaturar } from '../format';

// Acao recomendada em destaque + secundarias. Antes de confirmar, mostra a consequencia
// (no recalcular, De->Para via dry-run que NAO grava).
const DecisaoCiclo = ({ ciclo, isAdmin, onRecalcular, onReenviar, onFaturar, onContestar }) => {
    const [pendente, setPendente] = useState(null); // { tipo?, texto, run }
    const [prev, setPrev] = useState(null);          // 'carregando' | dados | null
    const chave = situacaoChave(ciclo);

    const podeRecalcular = ['contestado', 'expirado'].includes(ciclo.status);
    const podeReenviar = ['erro', 'aguardando', 'contestado', 'expirado'].includes(ciclo.status);
    const podeContestar = ciclo.status === 'aguardando';
    const podeFaturar = isAdmin && (['aprovado', 'expirado', 'contestado'].includes(ciclo.status) || chave === 'vencido');

    let primaria = null;
    if (chave === 'erro') primaria = 'reenviar';
    else if (chave === 'contestado') primaria = 'recalcular';
    else if (podeFaturar && (chave === 'aprovado' || chave === 'vencido' || chave === 'expirado')) primaria = 'faturar';

    const confirmarReenviar = () => setPendente({ texto: txtReenviarMesmo(ciclo.status, ciclo.valor_total, ciclo.cliente_nome), run: () => onReenviar(ciclo) });
    // Abre a confirmação e busca o preview De->Para (previewRecalculo NÃO grava).
    // Recalcular e Faturar compartilham essa busca; só o payload de `pendente` muda.
    // No faturar, se divergir (config mudou após o envio), o render avisa em vermelho — sem bloquear.
    const abrirComPreview = async (pendente) => {
        setPendente(pendente);
        setPrev('carregando');
        try {
            const { data } = await apiUser.get('/faturamento/ciclo/previewRecalculo', { params: { id_ciclo: ciclo.id } });
            setPrev(data.success ? data.dados : null);
        } catch { setPrev(null); }
    };
    const confirmarFaturar = () => abrirComPreview({ tipo: 'faturar', texto: txtFaturar(ciclo.valor_total, ciclo.cliente_nome), run: () => onFaturar(ciclo.id) });
    const confirmarRecalcular = () => abrirComPreview({ tipo: 'recalcular', texto: txtRecalcular(ciclo.cliente_nome), run: () => onRecalcular(ciclo.id) });
    const fechar = () => { setPendente(null); setPrev(null); };
    const runByKey = { recalcular: confirmarRecalcular, reenviar: confirmarReenviar, faturar: confirmarFaturar };
    const labelByKey = { recalcular: 'Recalcular valores e reenviar', reenviar: 'Reenviar', faturar: `Faturar ${fmtBRL(ciclo.valor_total)}` };

    const verde = { backgroundColor: '#9ac31c' };

    if (pendente) {
        return (
            <div className='bg-yellow-50 border border-yellow-300 rounded-xl p-4'>
                <p className='text-sm text-yellow-900 mb-2 font-medium'>{pendente.texto}</p>
                {pendente.tipo === 'recalcular' && (
                    <div className='text-sm mb-3'>
                        {prev === 'carregando' ? <span className='text-gray-500'>Calculando o novo valor…</span>
                            : prev ? (
                                <div className='bg-white rounded-lg p-3 border inline-flex items-center gap-2 flex-wrap'>
                                    <span className='text-gray-500'>De</span><span className='font-semibold'>{fmtBRL(prev.valor_atual)}</span>
                                    <span className='text-gray-400'>→</span>
                                    <span className='text-gray-500'>Para</span><span className='font-bold text-vox-blue-700'>{fmtBRL(prev.valor_novo)}</span>
                                    {Number(prev.diff) !== 0 && <span className={`text-xs font-semibold ${prev.diff > 0 ? 'text-green-700' : 'text-red-700'}`}>({prev.diff > 0 ? '+' : ''}{fmtBRL(prev.diff)})</span>}
                                </div>
                            ) : <span className='text-gray-500'>Não foi possível pré-calcular; o valor será recalculado no envio.</span>}
                    </div>
                )}
                {pendente.tipo === 'faturar' && (
                    <div className='text-sm mb-3'>
                        {prev === 'carregando' ? <span className='text-gray-500'>Conferindo com o cálculo ao vivo…</span>
                            : prev && Number(prev.diff) !== 0 ? (
                                <div className='bg-red-50 border border-red-300 rounded-lg p-3'>
                                    <p className='text-red-700 font-semibold mb-1'>⚠ O valor ao vivo mudou desde o envio (config alterada depois do congelamento).</p>
                                    <div className='inline-flex items-center gap-2 flex-wrap'>
                                        <span className='text-gray-500'>Vai faturar (congelado)</span><span className='font-semibold'>{fmtBRL(prev.valor_atual)}</span>
                                        <span className='text-gray-400'>·</span>
                                        <span className='text-gray-500'>ao vivo daria</span><span className='font-bold text-red-700'>{fmtBRL(prev.valor_novo)}</span>
                                        <span className='text-xs font-semibold text-red-700'>({prev.diff > 0 ? '+' : ''}{fmtBRL(prev.diff)})</span>
                                    </div>
                                    <p className='text-xs text-gray-500 mt-1'>Para cobrar o valor ao vivo, cancele e use “Recalcular”. Faturar assim cobra o congelado.</p>
                                </div>
                            ) : prev ? <span className='text-green-700'>✓ Valor confere com o cálculo ao vivo.</span>
                                : <span className='text-gray-500'>Não foi possível conferir agora; o valor congelado será faturado.</span>}
                    </div>
                )}
                <div className='flex gap-2'>
                    <button type='button' onClick={() => { const fn = pendente.run; fechar(); fn(); }} className='px-4 py-2 rounded-lg text-white font-semibold' style={verde}>Confirmar</button>
                    <button type='button' onClick={fechar} className='px-4 py-2 bg-gray-200 dark:bg-gray-600 dark:text-white rounded-lg'>Cancelar</button>
                </div>
            </div>
        );
    }

    return (
        <div className='space-y-3'>
            {primaria && (
                <div className='bg-vox-blue-50 border border-vox-blue-200 rounded-xl p-3'>
                    <p className='text-[11px] font-bold text-vox-blue-700 uppercase tracking-wide'>Recomendado</p>
                    <p className='text-sm text-gray-700 mb-2'>{motivoAcao(ciclo)}</p>
                    <button type='button' onClick={runByKey[primaria]}
                        className={`px-4 py-2 rounded-lg font-semibold ${primaria === 'reenviar' ? 'bg-vox-blue-600 text-white' : 'text-white'}`}
                        style={primaria === 'reenviar' ? undefined : verde}>{labelByKey[primaria]}</button>
                </div>
            )}
            <div className='flex gap-2 flex-wrap'>
                {podeRecalcular && primaria !== 'recalcular' && (
                    <button type='button' onClick={confirmarRecalcular} className='px-3 py-1.5 text-sm text-white rounded-lg font-semibold' style={verde}>Recalcular valores e reenviar</button>
                )}
                {podeReenviar && primaria !== 'reenviar' && (
                    <button type='button' onClick={confirmarReenviar} className='px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 dark:bg-gray-600 dark:text-white rounded-lg'>Reenviar o mesmo relatório</button>
                )}
                {podeContestar && (
                    <button type='button' onClick={onContestar} className='px-3 py-1.5 text-sm bg-orange-100 text-orange-800 hover:bg-orange-200 rounded-lg'>Registrar contestação</button>
                )}
                {podeFaturar && primaria !== 'faturar' && (
                    <button type='button' onClick={confirmarFaturar} className='px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 dark:bg-gray-600 dark:text-white rounded-lg'>{ciclo.status === 'contestado' ? 'Faturar mesmo assim' : 'Faturar'}</button>
                )}
            </div>
        </div>
    );
};

export default DecisaoCiclo;
